import { Check, CircleAlert, Pencil, Trash2, X } from 'lucide-react';
import { MouseEvent, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { AlertDto } from '@/Domain/api/Conversation/dto/Alert.dto';
import { AlertRunConditions } from '@/Domain/components/Insights/Alerts/AlertForm/AlertRunConditions';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { AlertFormValues } from '@/Domain/hooks/AI/useAlertForm/useAlertForm.hook';

type Props = {
  form: UseFormReturn<AlertFormValues>;
  index: number;
  alert: AlertDto;
  isEditMode: boolean;
  isUpdatePending: boolean;
  setIsEditMode: (mode: boolean) => void;
  onDelete: (alertId: number) => void;
  onReset: () => void;
  onToggleActive: (next: boolean) => void;
};

export const AlertFormItem = ({
  index,
  alert,
  form,
  onDelete,
  onReset,
  onToggleActive,
  isUpdatePending,
  isEditMode,
  setIsEditMode,
}: Props) => {
  const { t } = useTranslation('insights_page');

  const handleEditState = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsEditMode(true);
  };

  const handleDelete = () => onDelete(alert.id);

  const handleReset = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onReset();
    setIsEditMode(false);
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  return (
    <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
      <section className="col-span-full grid min-w-0 gap-y-4 @5xl:grid-cols-subgrid @5xl:items-start @5xl:px-4 @5xl:py-2">
        <p className="text-muted-foreground text-sm whitespace-nowrap @5xl:mt-2">
          {t('manage_alerts.form.alert', { index: index + 1 })}
        </p>

        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="min-w-0">
              <FormLabel className="@5xl:sr-only">{t('manage_alerts.form.header.name')}</FormLabel>
              <FormControl>
                {isEditMode ? (
                  <Input
                    autoFocus
                    placeholder={t('manage_alerts.form.name_placeholder')}
                    size="sm"
                    maxLength={100}
                    data-testid={`alert-name-${alert.id}`}
                    {...field}
                  />
                ) : (
                  <p className="text-sm break-words @5xl:px-4 @5xl:py-2">{alert.name}</p>
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem className="min-w-0">
              <FormLabel className="@5xl:sr-only">
                {t('manage_alerts.form.header.description')}
              </FormLabel>
              <FormControl>
                {isEditMode ? (
                  <AutosizeTextarea
                    maxLength={500}
                    autoComplete="off"
                    className="rounded-xl !min-h-20 !px-4 pb-2 pt-[9px] leading-snug break-words @5xl:!min-h-[34px]"
                    placeholder={t('manage_alerts.form.description_placeholder')}
                    minHeight={12}
                    data-testid={`alert-description-${alert.id}`}
                    {...field}
                  />
                ) : (
                  <p
                    className="text-sm whitespace-pre-line @5xl:px-4 @5xl:py-2"
                    style={{ wordBreak: 'break-word' }}
                  >
                    {alert.description}
                  </p>
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="run_conditions"
          render={({ field }) => (
            <FormItem className="min-w-0">
              <p className="text-sm font-semibold @5xl:sr-only">
                {t('manage_alerts.form.header.run_conditions')}
              </p>
              <AlertRunConditions
                conditions={field.value}
                isEditing={isEditMode}
                onChange={field.onChange}
                testIdPrefix={`alert-${alert.id}`}
              />
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex items-center justify-between gap-4 @5xl:contents">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 h-[34px] @5xl:justify-center">
                <label
                  htmlFor={`alert-active-${alert.id}`}
                  className="text-sm font-medium @5xl:sr-only"
                >
                  {t('manage_alerts.form.header.active')}
                </label>
                <Switch
                  id={`alert-active-${alert.id}`}
                  checked={form.watch('is_active')}
                  onCheckedChange={onToggleActive}
                  disabled={!alert.name || isEditMode || isUpdatePending}
                  data-testid={`alert-toggle-${alert.id}`}
                />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {form.watch('is_active')
                ? t('tooltip.deactivate_alert')
                : t('tooltip.activate_alert')}
            </TooltipContent>
          </Tooltip>

          {isEditMode ? (
            <section className="flex justify-end gap-2">
              <Button
                variant="secondary"
                size="icon"
                type="submit"
                disabled={isUpdatePending}
                aria-label={t('common:save')}
                data-testid={`alert-save-${alert.id}`}
              >
                <Check />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                disabled={!alert.name || isUpdatePending}
                onClick={handleReset}
                aria-label={t('common:cancel')}
                data-testid={`alert-cancel-${alert.id}`}
              >
                <X />
              </Button>
            </section>
          ) : (
            <section className="flex justify-end gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={handleEditState}
                    aria-label={t('common:edit')}
                    data-testid={`alert-edit-${alert.id}`}
                  >
                    <Pencil />
                  </Button>
                </TooltipTrigger>
                <TooltipContent align="end" alignOffset={-135}>
                  <Trans i18nKey="insights_page:tooltip.edit_alert" />
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild type="button">
                  <AlertDialogTrigger asChild type="button">
                    <Button
                      variant="secondary"
                      size="icon"
                      className="hover:!text-destructive"
                      aria-label={t('tooltip.delete_alert')}
                      data-testid={`alert-delete-trigger-${alert.id}`}
                    >
                      <Trash2 />
                    </Button>
                  </AlertDialogTrigger>
                </TooltipTrigger>
                <TooltipContent align="end">{t('tooltip.delete_alert')}</TooltipContent>
              </Tooltip>
            </section>
          )}
        </div>
      </section>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <CircleAlert className="size-4 bg-red-100 rounded-lg text-red-500 p-2 box-content" />
            {t('dialog.delete_alert.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>{t('dialog.delete_alert.description')}</AlertDialogDescription>
        </AlertDialogHeader>
        <Separator />
        <AlertDialogFooter>
          <AlertDialogCancel>
            <Button size="sm" variant="secondary">
              {t('dialog.delete_alert.button.cancel')}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete}>
            {t('dialog.delete_alert.button.delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
